import { useEffect, useState } from "react";
import { renderMarkdoc } from "@/lib/markdoc";
import { cn } from "@/lib/utils";

export type DocItem = { slug: string; title: string };

/** Section key from slug: "" | "mbsc-api" | "mbsc-react-sdk" */
function getSectionKey(slug: string): string {
  const i = slug.indexOf("/");
  return i === -1 ? "" : slug.slice(0, i);
}

const SECTION_LABELS: Record<string, string> = {
  "": "はじめに",
  "mbsc-api": "MBSC API",
  "mbsc-react-sdk": "MBSC React SDK",
};

/** Group docs by section and sort: はじめに → MBSC API → MBSC React SDK, items within section by slug */
function groupDocs(list: DocItem[]): { sectionKey: string; label: string; items: DocItem[] }[] {
  const bySection = new Map<string, DocItem[]>();
  for (const doc of list) {
    const key = getSectionKey(doc.slug);
    if (!bySection.has(key)) bySection.set(key, []);
    bySection.get(key)!.push(doc);
  }
  const order = ["", "mbsc-api", "mbsc-react-sdk"];
  return order.filter((k) => bySection.has(k)).map((sectionKey) => ({
    sectionKey,
    label: SECTION_LABELS[sectionKey] ?? sectionKey,
    items: (bySection.get(sectionKey) ?? []).sort((a, b) => a.slug.localeCompare(b.slug)),
  }));
}

type MbscDocsPageProps = {
  slug: string | null;
  onNavigate: (path: string) => void;
};

export function MbscDocsPage({ slug, onNavigate }: MbscDocsPageProps) {
  const [list, setList] = useState<DocItem[]>([]);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/mbsc-docs");
        if (!res.ok) throw new Error("Failed to load docs list");
        const data = (await res.json()) as DocItem[];
        if (!cancelled) setList(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const targetSlug =
      slug ??
      (list.find((d) => d.slug === "intro")?.slug ?? groupDocs(list)[0]?.items[0]?.slug ?? list[0]?.slug ?? null);
    if (!targetSlug) {
      setContent(null);
      return;
    }
    let cancelled = false;
    setContent(null);
    (async () => {
      try {
        const res = await fetch(`/api/mbsc-docs?slug=${encodeURIComponent(targetSlug)}`);
        if (!res.ok) {
          if (res.status === 404 && !cancelled) setContent(null);
          return;
        }
        const text = await res.text();
        if (!cancelled) setContent(text);
      } catch {
        if (!cancelled) setContent(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, list]);

  const defaultSlug =
    list.length > 0
      ? list.find((d) => d.slug === "intro")?.slug ?? groupDocs(list)[0]?.items[0]?.slug ?? list[0]!.slug
      : null;
  const currentSlug = slug ?? defaultSlug;
  const currentDoc = list.find((d) => d.slug === currentSlug);

  const handleNav = (path: string) => {
    onNavigate(path);
    if (path.startsWith("/service/mbsc/docs")) {
      window.scrollTo(0, 0);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 md:px-12">
        <p className="text-muted-foreground">読み込み中…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-12 md:px-12">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="border-t border-border/60">
      <div className="mx-auto max-w-6xl px-6 md:px-12">
        {/* Breadcrumb + title (system style: article page / MBSC sections) */}
        <header className="border-b border-border/60 py-8">
          <nav className="mb-6" aria-label="Breadcrumb">
            <button
              type="button"
              onClick={() => handleNav("/service")}
              className="cursor-pointer text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              サービス
            </button>
            <span className="mx-1.5 text-muted-foreground/60">/</span>
            <button
              type="button"
              onClick={() => handleNav("/service/mbsc")}
              className="cursor-pointer text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              MBSC
            </button>
            <span className="mx-1.5 text-muted-foreground/60">/</span>
            <span className="text-sm text-foreground" aria-current="page">
              ドキュメント
              {currentDoc ? ` / ${currentDoc.title}` : ""}
            </span>
          </nav>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Documentation
          </p>
          <h1 className="mt-2 font-serif text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-4xl">
            MBSC Docs
          </h1>
          <p className="mt-2 text-base text-muted-foreground">Monobundle Stablecoin Core Documentation</p>
        </header>

        <div className="flex">
          {/* Sidebar: desktop only */}
          <aside
            className="hidden w-56 shrink-0 border-r border-border/60 py-8 pr-8 md:block lg:w-64"
            aria-label="ドキュメント一覧"
          >
            <nav className="sticky top-24 space-y-4">
              {groupDocs(list).map(({ sectionKey, label, items }) => (
                <div key={sectionKey || "root"}>
                  <p className="mb-2 py-2.5 px-3 font-mono text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    {label}
                  </p>
                  <ul className="space-y-0.5">
                    {items.map((doc) => (
                      <li key={doc.slug}>
                        <a
                          href={`/service/mbsc/docs/${doc.slug}`}
                          onClick={(e) => {
                            e.preventDefault();
                            handleNav(`/service/mbsc/docs/${doc.slug}`);
                          }}
                          className={cn(
                            "block rounded-md px-3 py-2 text-sm transition-colors",
                            currentSlug === doc.slug
                              ? "bg-muted/60 font-medium text-foreground"
                              : "text-muted-foreground hover:bg-muted/30 hover:text-foreground",
                          )}
                        >
                          {doc.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <main className="min-w-0 flex-1 py-8 pl-0 md:pl-10">
            {/* Mobile doc picker (grouped) */}
            {list.length > 0 && (
              <div className="mb-6 md:hidden">
                <label htmlFor="mbsc-doc-picker" className="sr-only">
                  ドキュメントを選択
                </label>
                <select
                  id="mbsc-doc-picker"
                  value={currentSlug ?? ""}
                  onChange={(e) => {
                    const s = e.target.value;
                    if (s) handleNav(`/service/mbsc/docs/${s}`);
                  }}
                  className="w-full rounded-lg border border-border bg-muted/20 px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20"
                >
                  {groupDocs(list).map(({ label, items }) => (
                    <optgroup key={label} label={label}>
                      {items.map((doc) => (
                        <option key={doc.slug} value={doc.slug}>
                          {doc.title}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            {content === null && list.length === 0 && (
              <p className="text-muted-foreground">ドキュメントがありません。</p>
            )}
            {content === null && list.length > 0 && currentSlug && (
              <p className="text-muted-foreground">読み込み中…</p>
            )}
            {content !== null && (
              <div className="article-content mx-auto max-w-[680px]">
                {renderMarkdoc(content)}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
